import React, { useMemo, useState } from 'react';
import { Card, Row, Col, Typography, message, Modal } from 'antd';
import { PlusOutlined, SettingOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchSqlData, insertData, updateData, deleteData } from '../../../services/api';
import { useTranslation } from 'react-i18next';
import type { AdmVehicle } from '../../../types';
import StandardTable, { createActionColumn } from '../../../components/common/StandardTable';
import FilterBar from '../../../components/common/FilterBar';
import { Drawer, Form, DatePicker, Input, Select, InputNumber, Space } from 'antd';
import dayjs from 'dayjs';
import { setVehicleStatusIfNeeded } from '../vehicles/vehicleStatusSync';
import Button from '../../../components/common/Button';

const { Title } = Typography;

interface VehicleMaintenanceRow {
  id: number;
  vehicle_id: number;
  maintenance_date: string;
  description?: string | null;
  maintenance_type_id?: number | null;
  performed_at_km?: number | null;
  performed_at_hours?: number | null;
}

interface MaintType {
  id: number;
  name: string;
}

const MachMaintenanceList: React.FC = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage === 'tr' ? 'tr-TR' : 'en-US';
  const queryClient = useQueryClient();
  const invalidateVehicleQueries = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['adm_vehicle_maintenance_all'] }),
      queryClient.invalidateQueries({ queryKey: ['adm_vehicles_for_maint'] }),
      queryClient.invalidateQueries({ queryKey: ['machineryVehicles'] }),
      queryClient.invalidateQueries({ queryKey: ['machineryVehicleDetail'] }),
      queryClient.invalidateQueries({ queryKey: ['vehicleDetail'] }),
      queryClient.invalidateQueries({ queryKey: ['adm_maintenance_alerts'] }),
    ]);
  };
  const { data: maintenanceRows = [], isLoading } = useQuery<VehicleMaintenanceRow[], Error>({
    queryKey: ['adm_vehicle_maintenance_all'],
    queryFn: () => fetchSqlData('adm_vehicle_maintenance'),
    staleTime: 60_000,
  });
  const { data: vehicles = [] } = useQuery<AdmVehicle[], Error>({
    queryKey: ['adm_vehicles_for_maint'],
    queryFn: () => fetchSqlData('adm_vehicles'),
    staleTime: 5 * 60_000,
  });
  const { data: maintTypes = [] } = useQuery<MaintType[], Error>({
    queryKey: ['adm_maintenance_types'],
    queryFn: () => fetchSqlData('adm_maintenance_types'),
    staleTime: 10 * 60_000,
  });

  const vehiclesMap = useMemo(() => {
    const m: Record<number, string> = {};
    vehicles.forEach(v => {
      if (v.id) m[Number(v.id)] = `${v.license_plate || '-'} ${v.make || ''} ${v.model || ''}`.trim();
    });
    return m;
  }, [vehicles]);
  const maintTypeMap = useMemo(() => {
    const m: Record<number, string> = {};
    (maintTypes || []).forEach((t: any) => { if (t.id) m[t.id] = t.name; });
    return m;
  }, [maintTypes]);

  const [vehicleFilter, setVehicleFilter] = useState<number | undefined>(undefined);
  const [typeFilter, setTypeFilter] = useState<number | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const filtered = useMemo(() => {
    let arr = maintenanceRows;
    if (vehicleFilter !== undefined) arr = arr.filter(r => Number(r.vehicle_id) === Number(vehicleFilter));
    if (typeFilter !== undefined) arr = arr.filter(r => Number(r.maintenance_type_id) === Number(typeFilter));
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      arr = arr.filter(r =>
        (r.description || '').toLowerCase().includes(s) ||
        (vehiclesMap[Number(r.vehicle_id)] || '').toLowerCase().includes(s) ||
        (r.maintenance_type_id ? (maintTypeMap[r.maintenance_type_id] || '').toLowerCase().includes(s) : false)
      );
    }
    return arr;
  }, [maintenanceRows, vehicleFilter, typeFilter, searchTerm, vehiclesMap, maintTypeMap]);

  const columns = [
    { title: t('machinery.maintenance.common.vehicle'), dataIndex: 'vehicle_id', key: 'vehicle_id',
      render: (id: number) => vehiclesMap[Number(id)] || id,
      sorter: (a: VehicleMaintenanceRow, b: VehicleMaintenanceRow) => (vehiclesMap[a.vehicle_id] || '').localeCompare(vehiclesMap[b.vehicle_id] || '') },
    { title: t('machinery.maintenance.common.maintenanceType'), dataIndex: 'maintenance_type_id', key: 'maintenance_type_id',
      render: (id?: number) => id ? (maintTypeMap[id] || id) : '-', width: 140 },
    { title: t('machinery.maintenance.list.maintenanceDate'), dataIndex: 'maintenance_date', key: 'maintenance_date',
      render: (d: string) => d ? new Date(d).toLocaleString(locale) : '-', sorter: (a: VehicleMaintenanceRow, b: VehicleMaintenanceRow) => new Date(a.maintenance_date || 0).getTime() - new Date(b.maintenance_date || 0).getTime(), width: 180 },
    { title: t('machinery.maintenance.common.description'), dataIndex: 'description', key: 'description' },
    { title: t('machinery.maintenance.list.performedKm'), dataIndex: 'performed_at_km', key: 'performed_at_km', align: 'right' as const, width: 120 },
    { title: t('machinery.maintenance.list.performedHours'), dataIndex: 'performed_at_hours', key: 'performed_at_hours', align: 'right' as const, width: 120 },
  ];

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<VehicleMaintenanceRow | null>(null);

  const closeDrawer = () => {
    setOpen(false);
    setEditing(null);
  };

  const handleAdd = () => {
    setEditing(null);
    setOpen(true);
  };

  const handleEdit = (record: VehicleMaintenanceRow) => {
    setEditing(record);
    setOpen(true);
  };

  const handleDelete = (record: VehicleMaintenanceRow) => {
    Modal.confirm({
      title: t('machinery.maintenance.list.deleteConfirm'),
      okText: t('machinery.machines.common.yes'),
      cancelText: t('machinery.machines.common.no'),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteData('adm_vehicle_maintenance', 'id', record.id);
          message.success(t('machinery.maintenance.list.deleteSuccess'));
          queryClient.invalidateQueries({ queryKey: ['adm_vehicle_maintenance_all'] });
        } catch (e: any) {
          message.error(e?.response?.data?.error || e?.message || t('machinery.maintenance.list.deleteFailed'));
        }
      },
    });
  };

  return (
    <Card>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            <SettingOutlined style={{ marginRight: 8 }} />
            {t('machinery.maintenance.list.title')}
          </Title>
        </Col>
        <Col>
          <Button action="add" type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            {t('machinery.maintenance.list.addButton')}
          </Button>
        </Col>
      </Row>

      <FilterBar
        items={[
          { type: 'text', key: 'search', placeholder: t('machinery.maintenance.list.searchPlaceholder'), value: searchTerm, onChange: v => setSearchTerm(v), width: 300 },
          { type: 'select', key: 'vehicle', placeholder: t('machinery.maintenance.list.vehiclePlaceholder'), value: vehicleFilter as any, onChange: v => setVehicleFilter(v),
            options: vehicles.map(v => ({ value: v.id, label: vehiclesMap[Number(v.id)] || v.license_plate || String(v.id) })), width: 280, allowClear: true, showSearch: true },
          { type: 'select', key: 'type', placeholder: t('machinery.maintenance.list.typePlaceholder'), value: typeFilter as any, onChange: v => setTypeFilter(v),
            options: maintTypes.map(t => ({ value: t.id, label: t.name })), width: 220, allowClear: true, showSearch: true },
        ]}
        onReset={() => { setSearchTerm(''); setVehicleFilter(undefined); setTypeFilter(undefined); }}
      />

      <StandardTable
        rowKey="id"
        columns={[
          ...columns,
          createActionColumn<VehicleMaintenanceRow>({
            onEdit: handleEdit,
            onDelete: handleDelete,
            showView: false,
            align: 'left',
          }) as any,
        ]}
        dataSource={filtered}
        size="small"
        bordered
        pagination={{ pageSize: 20, showSizeChanger: true }}
        loading={isLoading}
      />

      <CreateMaintenanceDrawer
        open={open}
        onClose={closeDrawer}
        onSuccess={invalidateVehicleQueries}
        vehicles={vehicles}
        maintTypes={maintTypes}
        editing={editing}
      />
    </Card>
  );
};

export default MachMaintenanceList;

function CreateMaintenanceDrawer({
  open,
  onClose,
  onSuccess,
  vehicles,
  maintTypes,
  editing,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
  vehicles: AdmVehicle[];
  maintTypes: MaintType[];
  editing: VehicleMaintenanceRow | null;
}) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const vehicleOptions = (vehicles || []).map(v => ({ value: v.id, label: `${v.license_plate || ''} ${v.make || ''} ${v.model || ''}`.trim() || String(v.id) }));
  const typeOptions = (maintTypes || []).map((t: MaintType) => ({ value: t.id, label: t.name }));

  React.useEffect(() => {
    if (!open) {
      form.resetFields();
      return;
    }

    if (editing) {
      form.setFieldsValue({
        vehicle_id: editing.vehicle_id,
        maintenance_type_id: editing.maintenance_type_id ?? undefined,
        maintenance_date: editing.maintenance_date ? dayjs(editing.maintenance_date) : undefined,
        performed_at_km: editing.performed_at_km ?? undefined,
        performed_at_hours: editing.performed_at_hours ?? undefined,
        description: editing.description ?? undefined,
      });
      return;
    }

    form.resetFields();
  }, [open, editing, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const payload = {
        vehicle_id: values.vehicle_id,
        maintenance_type_id: values.maintenance_type_id ?? null,
        maintenance_date: values.maintenance_date?.toISOString(),
        performed_at_km: values.performed_at_km ?? null,
        performed_at_hours: values.performed_at_hours ?? null,
        description: values.description?.trim() || null,
      };

      if (editing?.id) {
        await updateData('adm_vehicle_maintenance', editing.id, payload);
        message.success(t('machinery.maintenance.list.updateSuccess'));
      } else {
        await insertData('adm_vehicle_maintenance', payload);
        message.success(t('machinery.maintenance.list.addSuccess'));
      }

      await setVehicleStatusIfNeeded(Number(values.vehicle_id), 'InMaintenance');
      await Promise.resolve(onSuccess());
      onClose();
      form.resetFields();
    } catch (e: any) {
      if (e?.errorFields) {
        return;
      }
      message.error(e?.message || t('machinery.maintenance.list.addFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer title={editing ? t('machinery.maintenance.list.editTitle') : t('machinery.maintenance.list.addTitle')} open={open} onClose={onClose} width={520} destroyOnClose>
      <Form form={form} layout="vertical">
        <Form.Item name="vehicle_id" label={t('machinery.maintenance.common.vehicle')} rules={[{ required: true, message: t('machinery.maintenance.list.vehicleRequired') }]}>
          <Select showSearch options={vehicleOptions} placeholder={t('machinery.maintenance.list.vehicleSelectPlaceholder')} />
        </Form.Item>
        <Form.Item name="maintenance_type_id" label={t('machinery.maintenance.common.maintenanceType')}>
          <Select showSearch allowClear options={typeOptions} placeholder={t('machinery.maintenance.list.typeSelectPlaceholder')} />
        </Form.Item>
        <Form.Item name="maintenance_date" label={t('machinery.maintenance.list.maintenanceDate')} rules={[{ required: true, message: t('machinery.maintenance.list.dateRequired') }]}>
          <DatePicker showTime style={{ width: '100%' }} />
        </Form.Item>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="performed_at_km" label={t('machinery.maintenance.list.performedKm')}>
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="performed_at_hours" label={t('machinery.maintenance.list.performedHours')}>
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="description" label={t('machinery.maintenance.common.description')}>
          <Input.TextArea rows={4} />
        </Form.Item>
        <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onClose}>{t('machinery.machines.common.cancel')}</Button>
          <Button action={editing ? 'edit' : 'add'} type="primary" onClick={handleSubmit} loading={loading}>
            {t('machinery.machines.common.save')}
          </Button>
        </Space>
      </Form>
    </Drawer>
  );
}

